import { z } from "zod";
/**
 * Bump when the shape changes in a way that invalidates cached parses.
 * The parse cache keys include this so stale entries can't survive a deploy.
 */
export const SCHEMA_VERSION = 5;
/** A library tag a user term resolved to. `name` is the canonical original-case label for the UI. */
export const matchedTagSchema = z.object({
    slug: z.string().min(1).max(80),
    name: z.string().min(1).max(120),
});
export const filterTermSchema = z.object({
    term: z.string().min(1).max(80),
    /**
     * Tags this term matched, server-resolved (fuzzy). Multiple because one
     * query word can map to a family of tags ("Latino" → Latin, Latina, …);
     * the query ORs them and the UI shows one removable chip per tag.
     */
    matchedTags: z.array(matchedTagSchema).max(50).optional(),
});
export const structuredFilterSchema = z.object({
    kind: z.enum(["photo", "video"]).optional(),
    duration: z
        .object({
        minSec: z.number().nonnegative().optional(),
        maxSec: z.number().nonnegative().optional(),
    })
        .optional(),
    takenAt: z
        .object({
        from: z.string().optional(),
        to: z.string().optional(),
    })
        .optional(),
    addedAt: z
        .object({
        from: z.string().optional(),
        to: z.string().optional(),
    })
        .optional(),
    include: z.array(filterTermSchema).max(20).optional(),
    exclude: z.array(filterTermSchema).max(20).optional(),
    favorites: z.boolean().optional(),
    orientation: z.enum(["portrait", "landscape", "square"]).optional(),
    minWidth: z.number().int().nonnegative().optional(),
    minHeight: z.number().int().nonnegative().optional(),
    exif: z
        .object({
        cameraMake: z.string().optional(),
        cameraModel: z.string().optional(),
        lensModel: z.string().optional(),
        location: z.string().optional(),
    })
        .optional(),
    ai: z
        .object({
        model: z.string().optional(),
        prompt: z.string().optional(),
        lora: z.string().optional(),
    })
        .optional(),
    name: z
        .object({
        include: z.array(z.string().min(1).max(80)).max(20).optional(),
        exclude: z.array(z.string().min(1).max(80)).max(20).optional(),
    })
        .optional(),
    rawQuery: z.string().max(2000),
    warnings: z.array(z.string()).max(10).optional(),
});
/**
 * Subset the LLM is asked to produce. Server fills in rawQuery, warnings,
 * and resolves matchedTags after the model responds.
 */
export const llmFilterOutputSchema = structuredFilterSchema
    .omit({ rawQuery: true, warnings: true })
    .extend({
    include: z.array(z.object({ term: z.string().min(1).max(80) })).max(20).optional(),
    exclude: z.array(z.object({ term: z.string().min(1).max(80) })).max(20).optional(),
});
